import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, BanknoteIcon, UserIcon, PhoneIcon, MailIcon, MapPinIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, PencilIcon, Trash2Icon, SaveIcon, XIcon, PlusIcon, ListIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, PackageIcon, UploadIcon, PaperclipIcon, EyeIcon, EyeOffIcon, CheckIcon, EditIcon, FileIcon, DownloadIcon, XCircle } from 'lucide-react';
import { Task } from '../components/TaskCard';
import { Customer } from '../components/CustomerCard';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { RichTextEditor } from '../components/RichTextEditor';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
interface StandardMaterial {
  id: string;
  name: string;
  price?: number;
}
interface TaskDetailProps {
  task: Task;
  customers: Customer[];
  onBack: () => void;
  onSave: (updatedTask: Task) => void;
  isReadOnly?: boolean;
}
export function TaskDetail({
  task,
  customers,
  onBack,
  onSave,
  isReadOnly = false
}: TaskDetailProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'done' | 'planned' | 'pending'>('in-progress');
  const [chargerType, setChargerType] = useState<'Zaptec 1' | 'Zaptec 2' | 'Andet'>('Zaptec 1');
  const [taskType, setTaskType] = useState<'Tilbudsopgave' | 'Regningsopgave'>('Tilbudsopgave');
  const [agreedPlanning, setAgreedPlanning] = useState('');
  const [agreedPlanningTime, setAgreedPlanningTime] = useState('09:00');
  const [agreedInstallation, setAgreedInstallation] = useState('');
  const [agreedInstallationTime, setAgreedInstallationTime] = useState('09:00');
  const [carDeliveryDate, setCarDeliveryDate] = useState('');
  const [hours, setHours] = useState('');
  const [materialPrice, setMaterialPrice] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [invoiced, setInvoiced] = useState('no');
  const [subTasks, setSubTasks] = useState('');
  const [materialInput, setMaterialInput] = useState('');
  const [materialPriceInput, setMaterialPriceInput] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [showMaterials, setShowMaterials] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [standardMaterials, setStandardMaterials] = useState<StandardMaterial[]>([]);
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null);
  const [editingMaterialValue, setEditingMaterialValue] = useState('');
  const [workLogDate, setWorkLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [workLogDescription, setWorkLogDescription] = useState('');
  const [workLogHours, setWorkLogHours] = useState('');
  const [workLog, setWorkLog] = useState<Array<{
    date: string;
    description: string;
    hours?: number;
  }>>([]);
  const [showWorkLog, setShowWorkLog] = useState(false);
  const [editingWorkLogIndex, setEditingWorkLogIndex] = useState<number | null>(null);
  const [editingWorkLogDate, setEditingWorkLogDate] = useState('');
  const [editingWorkLogDescription, setEditingWorkLogDescription] = useState('');
  const [editingWorkLogHours, setEditingWorkLogHours] = useState('');
  // Attachments state
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    pricing: true,
    materials: true,
    workLog: true,
    attachments: true
  });
  const [chargerPrices, setChargerPrices] = useState({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  // Load standard materials
  useEffect(() => {
    const docRef = doc(db, 'settings', 'standardMaterials');
    const unsubscribe = onSnapshot(docRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setStandardMaterials(data.items || []);
      }
    }, error => {
      console.error('Error loading standard materials:', error);
    });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) setChargerPrices(docSnap.data() as any);
      } catch (error) {
        console.error('Error loading charger prices:', error);
      }
    };
    loadPrices();
  }, []);
  useEffect(() => {
    if (task?.category === 'Ladeboks' && chargerType !== 'Andet') {
      setMaterialPrice(chargerPrices[chargerType].toString());
    }
  }, [chargerType, task?.category, chargerPrices]);
  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setCustomerId(task.customerId);
      setDescription(task.description || '');
      setStatus(task.status);
      setChargerType(task.chargerType || 'Zaptec 1');
      setTaskType(task.taskType || 'Tilbudsopgave');
      if (task.agreedPlanning) {
        const [date, time] = task.agreedPlanning.split('T');
        setAgreedPlanning(date);
        setAgreedPlanningTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedPlanning('');
        setAgreedPlanningTime('09:00');
      }
      if (task.agreedInstallation) {
        const [date, time] = task.agreedInstallation.split('T');
        setAgreedInstallation(date);
        setAgreedInstallationTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedInstallation('');
        setAgreedInstallationTime('09:00');
      }
      setCarDeliveryDate(task.carDeliveryDate || '');
      setHours(task.hours?.toString() || '');
      setMaterialPrice(task.materialPrice?.toString() || '');
      setHourlyRate(task.hourlyRate?.toString() || '');
      setQuotePrice(task.quotePrice?.toString() || '');
      setInvoiced(task.invoiced ? 'yes' : 'no');
      setSubTasks(task.subTasks || '');
      setMaterials(task.materials || []);
      setMaterialInput('');
      setMaterialPriceInput('');
      setBulkInput('');
      setBulkPrice('');
      setShowMaterials(false);
      setBulkMode(false);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
      setWorkLog(task.workLog || []);
      setWorkLogDate(new Date().toISOString().split('T')[0]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setShowWorkLog(false);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
      setAttachments(task.attachments || []);
    }
  }, [task]);
  const handleAddPresetMaterial = (preset: StandardMaterial) => {
    if (isReadOnly) return;
    setMaterials([...materials, preset.name]);
    if (preset.price !== undefined) {
      const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
      const newTotalPrice = currentPrice + preset.price;
      setMaterialPrice(newTotalPrice.toString());
    }
  };
  const totalHoursWithWorkLog = useMemo(() => {
    const baseHours = hours ? parseFloat(hours) : 0;
    const workLogHoursTotal = workLog.reduce((sum, entry) => sum + (entry.hours || 0), 0);
    return baseHours + workLogHoursTotal;
  }, [hours, workLog]);
  const calculations = useMemo(() => {
    const material = materialPrice ? parseFloat(materialPrice) : 0;
    const rate = hourlyRate ? parseFloat(hourlyRate) : 0;
    const time = totalHoursWithWorkLog;
    const quote = quotePrice ? parseFloat(quotePrice) : 0;
    if (taskType === 'Tilbudsopgave') {
      const profit = quote - material;
      const totalLaborCost = rate * time;
      return {
        totalPrice: quote,
        profit: quote > 0 || material > 0 ? profit : null,
        totalLaborCost: rate > 0 && time > 0 ? totalLaborCost : null
      };
    } else {
      const laborCost = rate * time;
      const totalPrice = laborCost + material;
      const profit = laborCost - material;
      return {
        totalPrice,
        profit: rate > 0 && time > 0 || material > 0 ? profit : null,
        totalLaborCost: rate > 0 && time > 0 ? laborCost : null
      };
    }
  }, [taskType, quotePrice, materialPrice, hourlyRate, totalHoursWithWorkLog]);
  const handleAddMaterial = () => {
    if (materialInput.trim()) {
      setMaterials([...materials, materialInput.trim()]);
      if (materialPriceInput.trim()) {
        const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
        const additionalPrice = parseFloat(materialPriceInput);
        setMaterialPrice((currentPrice + additionalPrice).toString());
      }
      setMaterialInput('');
      setMaterialPriceInput('');
    }
  };
  const handleAddBulkMaterials = () => {
    if (bulkInput.trim()) {
      const newMaterials = bulkInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (newMaterials.length > 0) {
        setMaterials([...materials, ...newMaterials]);
        if (bulkPrice.trim()) {
          const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
          setMaterialPrice((currentPrice + parseFloat(bulkPrice)).toString());
        }
        setBulkInput('');
        setBulkPrice('');
        setBulkMode(false);
      }
    }
  };
  const handleRemoveMaterial = (index: number) => setMaterials(materials.filter((_, i) => i !== index));
  const handleStartEditMaterial = (index: number) => {
    setEditingMaterialIndex(index);
    setEditingMaterialValue(materials[index]);
  };
  const handleSaveEditMaterial = () => {
    if (editingMaterialIndex !== null && editingMaterialValue.trim()) {
      const updatedMaterials = [...materials];
      updatedMaterials[editingMaterialIndex] = editingMaterialValue.trim();
      setMaterials(updatedMaterials);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
    }
  };
  const handleCancelEditMaterial = () => {
    setEditingMaterialIndex(null);
    setEditingMaterialValue('');
  };
  const handleAddWorkLog = () => {
    if (workLogDescription.trim() && workLogDate) {
      const entry: any = {
        date: workLogDate,
        description: workLogDescription.trim()
      };
      if (workLogHours.trim()) entry.hours = parseFloat(workLogHours);
      setWorkLog([...workLog, entry]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setWorkLogDate(new Date().toISOString().split('T')[0]);
    }
  };
  const handleRemoveWorkLog = (index: number) => setWorkLog(workLog.filter((_, i) => i !== index));
  const handleStartEditWorkLog = (index: number) => {
    const entry = workLog[index];
    setEditingWorkLogIndex(index);
    setEditingWorkLogDate(entry.date);
    setEditingWorkLogDescription(entry.description);
    setEditingWorkLogHours(entry.hours?.toString() || '');
  };
  const handleSaveEditWorkLog = () => {
    if (editingWorkLogIndex !== null && editingWorkLogDescription.trim() && editingWorkLogDate) {
      const updatedWorkLog = [...workLog];
      const entry: any = {
        date: editingWorkLogDate,
        description: editingWorkLogDescription.trim()
      };
      if (editingWorkLogHours.trim()) entry.hours = parseFloat(editingWorkLogHours);
      updatedWorkLog[editingWorkLogIndex] = entry;
      setWorkLog(updatedWorkLog);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
    }
  };
  const handleCancelEditWorkLog = () => {
    setEditingWorkLogIndex(null);
    setEditingWorkLogDate('');
    setEditingWorkLogDescription('');
    setEditingWorkLogHours('');
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !task) return;
    try {
      setIsUploading(true);
      const storagePath = `tasks/${task.id}/bilag/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newAttachment: TaskAttachment = {
        name: file.name,
        url,
        storagePath,
        uploadedAt: new Date().toISOString(),
        size: file.size
      };
      setAttachments(prev => [...prev, newAttachment]);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Der opstod en fejl under upload af filen.');
    } finally {
      setIsUploading(false);
      // Reset input
      e.target.value = '';
    }
  };
  const handleDeleteAttachment = async (attachment: TaskAttachment, index: number) => {
    if (!confirm('Er du sikker på, at du vil slette dette bilag?')) return;
    try {
      const storageRef = ref(storage, attachment.storagePath);
      await deleteObject(storageRef);
      setAttachments(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Der opstod en fejl under sletning af filen.');
    }
  };
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  const toggleAllSections = () => {
    const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
    setExpandedSections({
      pricing: !allExpanded,
      materials: !allExpanded,
      workLog: !allExpanded,
      attachments: !allExpanded
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !title.trim() || !customerId) return;
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    const planningDateTime = agreedPlanning ? `${agreedPlanning}T${agreedPlanningTime}:00` : undefined;
    const installationDateTime = agreedInstallation ? `${agreedInstallation}T${agreedInstallationTime}:00` : undefined;
    onSave({
      ...task,
      title,
      customerId,
      customerName: customer.name,
      description,
      status,
      chargerType: task.category === 'Ladeboks' ? chargerType : undefined,
      taskType,
      agreedPlanning: planningDateTime,
      agreedInstallation: installationDateTime,
      carDeliveryDate: carDeliveryDate || undefined,
      hours: hours ? parseFloat(hours) : undefined,
      materialPrice: materialPrice ? parseFloat(materialPrice) : undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      quotePrice: quotePrice ? parseFloat(quotePrice) : undefined,
      totalPrice: calculations.totalPrice || undefined,
      invoiced: invoiced === 'yes',
      subTasks: subTasks || undefined,
      materials: materials.length > 0 ? materials : undefined,
      workLog: workLog.length > 0 ? workLog : undefined,
      attachments: attachments.length > 0 ? attachments : undefined
    });
    onBack();
  };
  if (!task) return null;
  const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
  return <div className="min-h-screen bg-stone-50 py-8" data-id="element-2173">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2174">
        <button onClick={onBack} className="flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors mb-6" data-id="element-2175">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-2176" />
          <span className="font-medium" data-id="element-2177">Tilbage</span>
        </button>

        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap" data-id="element-2178">
          <div data-id="element-2179">
            <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-2180">
              Rediger opgave
            </h1>
            {task?.taskId && <p className="text-stone-500 mt-2" data-id="element-2181">
                Opgave-ID:{' '}
                <span className="font-medium text-teal-600" data-id="element-2182">{task.taskId}</span>
              </p>}
          </div>
          {!isReadOnly && <Button type="button" variant="primary" onClick={handleSubmit as any} className="hidden sm:flex" data-id="element-2183">
              Gem ændringer
            </Button>}
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4 sm:p-8" data-id="element-2184">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2185">
            {/* Basic Information */}
            <div data-id="element-2186">
              <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-2187">
                Grundlæggende information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2188">
                <Input label="Opgavetitel" value={title} onChange={e => setTitle(e.target.value)} placeholder="Indtast opgavetitel" required disabled={isReadOnly} data-id="element-2189" />

                <Select label="Kunde" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
                value: '',
                label: 'Vælg en kunde'
              }, ...customers.map(c => ({
                value: c.id,
                label: `${c.name} (${c.customerId})`
              }))]} required disabled={isReadOnly} data-id="element-2190" />
              </div>

              <div className="mt-4" data-id="element-2191">
                <RichTextEditor label="Beskrivelse" value={description} onChange={setDescription} placeholder="Beskriv opgaven" rows={3} disabled={isReadOnly} data-id="element-2192" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-2193">
                <Select label="Status" value={status} onChange={e => setStatus(e.target.value as 'todo' | 'in-progress' | 'done' | 'planned' | 'pending')} options={[{
                value: 'planned',
                label: 'Planlagt'
              }, {
                value: 'pending',
                label: 'Afventende'
              }, {
                value: 'in-progress',
                label: 'I gang'
              }, {
                value: 'done',
                label: 'Afsluttet'
              }]} disabled={isReadOnly} data-id="element-2194" />

                <Select label="Faktureret" value={invoiced} onChange={e => setInvoiced(e.target.value)} options={[{
                value: 'no',
                label: 'Nej'
              }, {
                value: 'yes',
                label: 'Ja'
              }]} disabled={isReadOnly} data-id="element-2195" />
              </div>
            </div>

            {/* Dates */}
            <div data-id="element-2196">
              <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-2197">
                Datoer
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2198">
                <div data-id="element-2199">
                  <Input label="Aftalt projektering" type="date" value={agreedPlanning} onChange={e => setAgreedPlanning(e.target.value)} disabled={isReadOnly} data-id="element-2200" />
                  {agreedPlanning && <div className="mt-2" data-id="element-2201">
                      <Input label="Tidspunkt" type="time" value={agreedPlanningTime} onChange={e => setAgreedPlanningTime(e.target.value)} disabled={isReadOnly} data-id="element-2202" />
                    </div>}
                </div>

                <div data-id="element-2203">
                  <Input label="Aftalt installation" type="date" value={agreedInstallation} onChange={e => setAgreedInstallation(e.target.value)} disabled={isReadOnly} data-id="element-2204" />
                  {agreedInstallation && <div className="mt-2" data-id="element-2205">
                      <Input label="Tidspunkt" type="time" value={agreedInstallationTime} onChange={e => setAgreedInstallationTime(e.target.value)} disabled={isReadOnly} data-id="element-2206" />
                    </div>}
                </div>
              </div>

              {task.category === 'Ladeboks' && <div className="mt-4" data-id="element-2207">
                  <Input label="Leveringsdato på bil (valgfrit)" type="date" value={carDeliveryDate} onChange={e => setCarDeliveryDate(e.target.value)} disabled={isReadOnly} data-id="element-2208" />
                  <p className="text-xs text-stone-500 mt-1" data-id="element-2209">
                    Angiv hvornår bilen leveres til kunden
                  </p>
                </div>}
            </div>

            {/* Expand/Collapse All Button */}
            {!isReadOnly && <div className="flex justify-end" data-id="element-2210">
                <button type="button" onClick={toggleAllSections} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors" data-id="element-2211">
                  {allExpanded ? <>
                      <ChevronUpIcon className="w-4 h-4" data-id="element-2212" />
                      Skjul alle sektioner
                    </> : <>
                      <ChevronDownIcon className="w-4 h-4" data-id="element-2213" />
                      Udvid alle sektioner
                    </>}
                </button>
              </div>}

            {/* PRICING SECTION */}
            <AnimatePresence data-id="element-2214">
              {expandedSections.pricing ? <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2215">
                  <div data-id="element-2216">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  pricing: false
                })} className="flex items-center justify-between w-full mb-6 group" data-id="element-2217">
                      <h3 className="text-lg font-semibold text-stone-900" data-id="element-2218">
                        Priser og timer
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-2219">
                        {totalHoursWithWorkLog > 0 && <span className="text-sm text-stone-500" data-id="element-2220">
                            Total: {totalHoursWithWorkLog.toFixed(1)}t
                          </span>}
                        <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2221" />
                      </div>
                    </button>

                    <div className="space-y-6" data-id="element-2222">
                      {task.category === 'Ladeboks' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} className="space-y-2" data-id="element-2223">
                          <label className="block text-sm font-medium text-stone-700" data-id="element-2224">
                            Ladeboks type
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2225">
                            {(['Zaptec 1', 'Zaptec 2', 'Andet'] as const).map(type => <button key={type} type="button" onClick={() => setChargerType(type)} disabled={isReadOnly} className={`p-4 rounded-xl border text-left transition-all ${chargerType === type ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500' : 'bg-white border-stone-200 hover:border-stone-300'} ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2226">
                                  <div className="font-medium text-stone-900" data-id="element-2227">
                                    {type}
                                  </div>
                                  {type !== 'Andet' && <div className="text-sm text-stone-500 mt-1" data-id="element-2228">
                                      {chargerPrices[type].toLocaleString('da-DK')}{' '}
                                      kr
                                    </div>}
                                </button>)}
                          </div>
                        </motion.div>}

                      <div className="pt-2" data-id="element-2229">
                        <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-2230">
                          Opgavetype
                        </label>
                        <div className="flex p-1 bg-stone-50 rounded-xl border border-stone-200 w-fit" data-id="element-2231">
                          {(['Tilbudsopgave', 'Regningsopgave'] as const).map(type => <button key={type} type="button" onClick={() => setTaskType(type)} disabled={isReadOnly} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${taskType === type ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'} ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2232">
                                {type}
                              </button>)}
                        </div>
                      </div>

                      <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200" data-id="element-2233">
                        <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide mb-6" data-id="element-2234">
                          Timer & Pris
                        </h3>

                        {taskType === 'Tilbudsopgave' ? <div className="space-y-6" data-id="element-2235">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2236">
                              <div data-id="element-2237">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2238">
                                  Tilbudspris (kr){' '}
                                  <span className="text-red-500" data-id="element-2239">*</span>
                                </label>
                                <input type="number" step="0.01" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} placeholder="f.eks. 8000" required disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2240" />
                                <p className="text-xs text-stone-500 mt-1.5" data-id="element-2241">
                                  Den samlede pris kunden skal betale
                                </p>
                              </div>

                              <div data-id="element-2242">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2243">
                                  Materialer pris (kr)
                                </label>
                                <input type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={isReadOnly || task.category === 'Ladeboks' && chargerType !== 'Andet'} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2244" />
                                {task.category === 'Ladeboks' && chargerType !== 'Andet' ? <p className="text-xs text-stone-500 mt-1.5" data-id="element-2245">
                                    Udfyldes automatisk baseret på ladeboks type
                                  </p> : <p className="text-xs text-stone-500 mt-1.5" data-id="element-2246">
                                    Dine udgifter til materialer
                                  </p>}
                              </div>
                            </div>

                            <div className="pt-4 border-t border-stone-200 space-y-6" data-id="element-2247">
                              <div data-id="element-2248">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2249">
                                  Timepris (kr)
                                </label>
                                <input type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2250" />
                                <p className="text-xs text-stone-500 mt-1.5" data-id="element-2251">
                                  Din pris per time (bruges til udregning)
                                </p>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2252">
                                <div data-id="element-2253">
                                  <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2254">
                                    Timer brugt
                                  </label>
                                  <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2255" />
                                  <p className="text-xs text-stone-500 mt-1.5" data-id="element-2256">
                                    Bruges til at udregne din reelle timepris
                                  </p>
                                </div>

                                <div className="bg-white p-4 rounded-xl border border-stone-200 flex flex-col justify-center gap-3" data-id="element-2257">
                                  <div data-id="element-2258">
                                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2259">
                                      Samlet pris
                                    </p>
                                    <p className="text-xl font-bold text-stone-900 mt-0.5" data-id="element-2260">
                                      {calculations.totalPrice.toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                      kr
                                    </p>
                                  </div>
                                  <div className="pt-3 border-t border-stone-100" data-id="element-2261">
                                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2262">
                                      Pris kun for timer
                                    </p>
                                    <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-2263">
                                      {(calculations.totalPrice - (materialPrice ? parseFloat(materialPrice) : 0)).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                      kr
                                    </p>
                                    <p className="text-xs text-stone-400 mt-1" data-id="element-2264">
                                      Tilbudspris − Materialer
                                    </p>
                                  </div>
                                  {totalHoursWithWorkLog > 0 && <div className="pt-3 border-t border-stone-100" data-id="element-2265">
                                      <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2266">
                                        Timer brugt i alt
                                      </p>
                                      <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-2267">
                                        {totalHoursWithWorkLog.toFixed(1)} timer
                                      </p>
                                    </div>}
                                </div>
                              </div>
                            </div>
                          </div> : <div className="space-y-6" data-id="element-2268">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2269">
                              <div data-id="element-2270">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2271">
                                  Timepris (kr){' '}
                                  <span className="text-red-500" data-id="element-2272">*</span>
                                </label>
                                <input type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" required disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2273" />
                                <p className="text-xs text-stone-500 mt-1.5" data-id="element-2274">
                                  Din pris per time
                                </p>
                              </div>

                              <div data-id="element-2275">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2276">
                                  Materialer pris (kr)
                                </label>
                                <input type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={isReadOnly || task.category === 'Ladeboks' && chargerType !== 'Andet'} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2277" />
                                {task.category === 'Ladeboks' && chargerType !== 'Andet' ? <p className="text-xs text-stone-500 mt-1.5" data-id="element-2278">
                                    Udfyldes automatisk baseret på ladeboks type
                                  </p> : <p className="text-xs text-stone-500 mt-1.5" data-id="element-2279">
                                    Dine udgifter til materialer
                                  </p>}
                              </div>
                            </div>

                            <div className="pt-4 border-t border-stone-200" data-id="element-2280">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2281">
                                <div data-id="element-2282">
                                  <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2283">
                                    Timer brugt{' '}
                                    <span className="text-red-500" data-id="element-2284">*</span>
                                  </label>
                                  <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" required disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2285" />
                                  <p className="text-xs text-stone-500 mt-1.5" data-id="element-2286">
                                    Antal timer brugt på opgaven
                                  </p>
                                </div>

                                <div className="bg-white p-4 rounded-xl border border-stone-200 flex flex-col justify-center gap-3" data-id="element-2287">
                                  <div data-id="element-2288">
                                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2289">
                                      Samlet pris
                                    </p>
                                    <p className="text-xl font-bold text-stone-900 mt-0.5" data-id="element-2290">
                                      {calculations.totalPrice.toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                      kr
                                    </p>
                                  </div>
                                  <div className="pt-3 border-t border-stone-100" data-id="element-2291">
                                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2292">
                                      Pris kun for timer
                                    </p>
                                    <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-2293">
                                      {(calculations.totalLaborCost ?? 0).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                      kr
                                    </p>
                                    <p className="text-xs text-stone-400 mt-1" data-id="element-2294">
                                      Timepris × Timer
                                    </p>
                                  </div>
                                  {totalHoursWithWorkLog > 0 && <div className="pt-3 border-t border-stone-100" data-id="element-2295">
                                      <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2296">
                                        Timer brugt i alt
                                      </p>
                                      <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-2297">
                                        {totalHoursWithWorkLog.toFixed(1)} timer
                                      </p>
                                    </div>}
                                </div>
                              </div>
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2298">
                  <button type="button" onClick={() => setExpandedSections({
                ...expandedSections,
                pricing: true
              })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2299">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2300" />
                    Vis Priser og timer
                    {totalHoursWithWorkLog > 0 && <span className="text-stone-500" data-id="element-2301">
                        (Total: {totalHoursWithWorkLog.toFixed(1)}t)
                      </span>}
                  </button>
                </motion.div>}
            </AnimatePresence>

            {/* MATERIALS SECTION */}
            <AnimatePresence data-id="element-2302">
              {expandedSections.materials ? <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2303">
                  <div data-id="element-2304">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  materials: false
                })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2305">
                      <h3 className="text-lg font-semibold text-stone-900" data-id="element-2306">
                        Materialer
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-2307">
                        {materials.length > 0 && <span className="text-sm text-stone-500" data-id="element-2308">
                            {materials.length} materialer
                          </span>}
                        <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2309" />
                      </div>
                    </button>

                    <div className="space-y-3" data-id="element-2310">
                      {!isReadOnly && <>
                          <div className="flex items-center justify-between mb-4" data-id="element-2311">
                            <div className="flex-1" data-id="element-2312" />
                            <button type="button" onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-2313">
                              <ListIcon className="w-4 h-4" data-id="element-2314" />
                              {bulkMode ? 'Enkelt tilføjelse' : 'Tilføj ordre'}
                            </button>
                          </div>

                          <AnimatePresence mode="wait" data-id="element-2315">
                            {bulkMode ? <motion.div key="bulk" initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} transition={{
                        duration: 0.2
                      }} className="space-y-3" data-id="element-2316">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-2317">
                                  <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-2318">
                                    📦 Tilføj stor ordre
                                  </p>
                                  <p className="text-xs text-blue-700" data-id="element-2319">
                                    Skriv ét materiale per linje. Prisen lægges
                                    automatisk til materialer pris.
                                  </p>
                                </div>

                                <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)} placeholder="Kabel 10m Stikkontakt Sikring 16A Kabelkanal 2m ..." rows={6} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none font-mono text-sm" data-id="element-2320" />

                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-2321">
                                  <label className="block text-sm font-medium text-amber-900 mb-2" data-id="element-2322">
                                    💰 Pris for denne ordre (kr)
                                  </label>
                                  <input type="number" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="f.eks. 2500" className="w-full px-4 py-2.5 rounded-lg border border-amber-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all" data-id="element-2323" />
                                  {bulkPrice && materialPrice && <div className="mt-3 pt-3 border-t border-amber-200" data-id="element-2324">
                                      <div className="flex items-center justify-between text-sm" data-id="element-2325">
                                        <span className="text-amber-700" data-id="element-2326">
                                          Nuværende materialer pris:
                                        </span>
                                        <span className="font-medium text-amber-900" data-id="element-2327">
                                          {parseFloat(materialPrice).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-sm mt-1" data-id="element-2328">
                                        <span className="text-amber-700" data-id="element-2329">
                                          Ordre pris:
                                        </span>
                                        <span className="font-medium text-amber-900" data-id="element-2330">
                                          +
                                          {parseFloat(bulkPrice).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-base mt-2 pt-2 border-t border-amber-300" data-id="element-2331">
                                        <span className="font-semibold text-amber-900" data-id="element-2332">
                                          Ny total:
                                        </span>
                                        <span className="font-bold text-amber-900" data-id="element-2333">
                                          {(parseFloat(materialPrice) + parseFloat(bulkPrice)).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                    </div>}
                                </div>

                                <div className="flex gap-2" data-id="element-2334">
                                  <Button type="button" onClick={handleAddBulkMaterials} variant="primary" className="flex-1 flex items-center justify-center gap-2 py-2.5" disabled={!bulkInput.trim()} data-id="element-2335">
                                    <PlusIcon className="w-4 h-4" data-id="element-2336" />
                                    Tilføj alle materialer
                                    {bulkPrice && ` (+${parseFloat(bulkPrice).toLocaleString('da-DK')} kr)`}
                                  </Button>
                                  <Button type="button" onClick={() => {
                            setBulkInput('');
                            setBulkPrice('');
                            setBulkMode(false);
                          }} variant="secondary" className="py-2.5 px-6" data-id="element-2337">
                                    Annuller
                                  </Button>
                                </div>
                              </motion.div> : <motion.div key="single" initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} transition={{
                        duration: 0.2
                      }} className="space-y-6" data-id="element-2338">
                                {/* Standard Materials */}
                                <div data-id="element-2339">
                                  <h4 className="text-sm font-medium text-stone-700 mb-3" data-id="element-2340">
                                    Vælg fra standard
                                  </h4>
                                  {standardMaterials.length === 0 ? <p className="text-sm text-stone-500 italic" data-id="element-2341">
                                      Ingen standard materialer endnu — tilføj
                                      dem under Information.
                                    </p> : <div className="flex flex-wrap gap-2" data-id="element-2342">
                                      {standardMaterials.map(preset => <motion.button key={preset.id} type="button" whileTap={!isReadOnly ? {
                              scale: 0.95
                            } : undefined} onClick={() => handleAddPresetMaterial(preset)} disabled={isReadOnly} className={`px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-sm font-medium text-stone-700 transition-all flex items-center gap-2 ${isReadOnly ? 'opacity-70 cursor-not-allowed' : 'hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700'}`} data-id="element-2343">
                                          <PackageIcon className="w-4 h-4" data-id="element-2344" />
                                          {preset.name}
                                          {preset.price !== undefined && <span className="text-stone-500 font-normal" data-id="element-2345">
                                              ·{' '}
                                              {preset.price.toLocaleString('da-DK')}{' '}
                                              kr
                                            </span>}
                                        </motion.button>)}
                                    </div>}
                                </div>

                                <div className="pt-4 border-t border-stone-100" data-id="element-2346">
                                  <h4 className="text-sm font-medium text-stone-700 mb-3" data-id="element-2347">
                                    Tilføj manuelt
                                  </h4>
                                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-2348">
                                    <input type="text" value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMaterial();
                              }
                            }} placeholder="Skriv materiale navn..." className="flex-1 px-3 py-2 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" data-id="element-2349" />
                                    <input type="number" step="0.01" value={materialPriceInput} onChange={e => setMaterialPriceInput(e.target.value)} onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMaterial();
                              }
                            }} placeholder="Pris (kr)" className="w-full sm:w-32 px-3 py-2 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" data-id="element-2350" />
                                    <Button type="button" onClick={handleAddMaterial} variant="primary" className="flex items-center justify-center gap-2" disabled={!materialInput.trim()} data-id="element-2351">
                                      <PlusIcon className="w-4 h-4" data-id="element-2352" />
                                      Tilføj
                                    </Button>
                                  </div>
                                </div>

                                {materialPriceInput && materialPrice && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-green-50 border border-green-200 rounded-lg p-3" data-id="element-2353">
                                    <div className="flex items-center justify-between text-sm" data-id="element-2354">
                                      <span className="text-green-700" data-id="element-2355">
                                        Nuværende total:
                                      </span>
                                      <span className="font-medium text-green-900" data-id="element-2356">
                                        {parseFloat(materialPrice).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1" data-id="element-2357">
                                      <span className="text-green-700" data-id="element-2358">
                                        Tilføjes:
                                      </span>
                                      <span className="font-medium text-green-900" data-id="element-2359">
                                        +
                                        {parseFloat(materialPriceInput).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-green-300" data-id="element-2360">
                                      <span className="font-semibold text-green-900" data-id="element-2361">
                                        Ny total:
                                      </span>
                                      <span className="font-bold text-green-900" data-id="element-2362">
                                        {(parseFloat(materialPrice) + parseFloat(materialPriceInput)).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                  </motion.div>}
                              </motion.div>}
                          </AnimatePresence>
                        </>}

                      {materials.length > 0 && <div className="space-y-2 mt-4" data-id="element-2363">
                          <Button type="button" onClick={() => setShowMaterials(!showMaterials)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-2364">
                            {showMaterials ? <>
                                <EyeOffIcon className="w-4 h-4" data-id="element-2365" />
                                Skjul materialer ({materials.length})
                              </> : <>
                                <EyeIcon className="w-4 h-4" data-id="element-2366" />
                                Se materialer ({materials.length})
                              </>}
                          </Button>

                          <AnimatePresence data-id="element-2367">
                            {showMaterials && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.2
                      }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-2368">
                                <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-2369">
                                  {materials.map((material, index) => <motion.li key={index} initial={{
                            opacity: 0,
                            x: -10
                          }} animate={{
                            opacity: 1,
                            x: 0
                          }} exit={{
                            opacity: 0,
                            x: -10
                          }} transition={{
                            delay: index * 0.02
                          }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-2370">
                                      {editingMaterialIndex === index ? <div className="flex items-center gap-2" data-id="element-2371">
                                          <input type="text" value={editingMaterialValue} onChange={e => setEditingMaterialValue(e.target.value)} onKeyPress={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveEditMaterial();
                                } else if (e.key === 'Escape') {
                                  handleCancelEditMaterial();
                                }
                              }} className="flex-1 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" autoFocus data-id="element-2372" />
                                          <button type="button" onClick={handleSaveEditMaterial} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded transition-colors" data-id="element-2373">
                                            <CheckIcon className="w-4 h-4" data-id="element-2374" />
                                          </button>
                                          <button type="button" onClick={handleCancelEditMaterial} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded transition-colors" data-id="element-2375">
                                            <XCircle className="w-4 h-4" data-id="element-2376" />
                                          </button>
                                        </div> : <div className="flex items-center justify-between" data-id="element-2377">
                                          <span className="text-sm text-stone-900" data-id="element-2378">
                                            {material}
                                          </span>
                                          {!isReadOnly && <div className="flex items-center gap-1" data-id="element-2379">
                                              <button type="button" onClick={() => handleStartEditMaterial(index)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger materiale" data-id="element-2380">
                                                <EditIcon className="w-4 h-4" data-id="element-2381" />
                                              </button>
                                              <button type="button" onClick={() => handleRemoveMaterial(index)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern materiale" data-id="element-2382">
                                                <XCircle className="w-4 h-4" data-id="element-2383" />
                                              </button>
                                            </div>}
                                        </div>}
                                    </motion.li>)}
                                </ul>
                              </motion.div>}
                          </AnimatePresence>
                        </div>}

                      {materials.length === 0 && !bulkMode && <p className="text-sm text-stone-500 text-center py-4 bg-stone-50 rounded-lg border border-stone-200 mt-3" data-id="element-2384">
                          Ingen materialer tilføjet endnu
                        </p>}
                    </div>
                  </div>
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2385">
                  <button type="button" onClick={() => setExpandedSections({
                ...expandedSections,
                materials: true
              })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2386">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2387" />
                    Vis Materialer
                    {materials.length > 0 && <span className="text-stone-500" data-id="element-2388">
                        ({materials.length} materialer)
                      </span>}
                  </button>
                </motion.div>}
            </AnimatePresence>

            {/* WORK LOG SECTION */}
            <AnimatePresence data-id="element-2389">
              {expandedSections.workLog ? <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2390">
                  <div data-id="element-2391">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  workLog: false
                })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2392">
                      <h3 className="text-lg font-semibold text-stone-900" data-id="element-2393">
                        Arbejdslog
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-2394">
                        {workLog.length > 0 && <span className="text-sm text-stone-500" data-id="element-2395">
                            {workLog.length} entries
                          </span>}
                        <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2396" />
                      </div>
                    </button>

                    <div className="space-y-3" data-id="element-2397">
                      {!isReadOnly && <div className="space-y-3" data-id="element-2398">
                          <div className="flex gap-2" data-id="element-2399">
                            <input type="date" value={workLogDate} onChange={e => setWorkLogDate(e.target.value)} className="w-40 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2400" />
                            <input type="number" step="0.5" value={workLogHours} onChange={e => setWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2401" />
                          </div>
                          <textarea value={workLogDescription} onChange={e => setWorkLogDescription(e.target.value)} placeholder="Beskriv hvad der blev lavet...&#10;Du kan skrive flere linjer her." rows={4} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-2402" />
                          <Button type="button" onClick={handleAddWorkLog} variant="primary" className="flex items-center gap-2 w-full justify-center" disabled={!workLogDescription.trim()} data-id="element-2403">
                            <PlusIcon className="w-4 h-4" data-id="element-2404" />
                            Tilføj til arbejdslog
                          </Button>
                        </div>}

                      {workLog.length > 0 && <div className="space-y-2 mt-4" data-id="element-2405">
                          <Button type="button" onClick={() => setShowWorkLog(!showWorkLog)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-2406">
                            {showWorkLog ? <>
                                <EyeOffIcon className="w-4 h-4" data-id="element-2407" />
                                Skjul arbejdslog ({workLog.length})
                              </> : <>
                                <FileTextIcon className="w-4 h-4" data-id="element-2408" />
                                Se arbejdslog ({workLog.length})
                              </>}
                          </Button>

                          <AnimatePresence data-id="element-2409">
                            {showWorkLog && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.2
                      }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-2410">
                                <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-2411">
                                  {workLog.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((entry, index) => {
                            const originalIndex = workLog.findIndex(e => e === entry);
                            return <motion.li key={originalIndex} initial={{
                              opacity: 0,
                              x: -10
                            }} animate={{
                              opacity: 1,
                              x: 0
                            }} exit={{
                              opacity: 0,
                              x: -10
                            }} transition={{
                              delay: index * 0.02
                            }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-2412">
                                          {editingWorkLogIndex === originalIndex ? <div className="space-y-2" data-id="element-2413">
                                              <div className="flex gap-2" data-id="element-2414">
                                                <input type="date" value={editingWorkLogDate} onChange={e => setEditingWorkLogDate(e.target.value)} className="w-40 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2415" />
                                                <input type="number" step="0.5" value={editingWorkLogHours} onChange={e => setEditingWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2416" />
                                              </div>
                                              <textarea value={editingWorkLogDescription} onChange={e => setEditingWorkLogDescription(e.target.value)} rows={3} className="w-full px-3 py-2 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" autoFocus data-id="element-2417" />
                                              <div className="flex gap-2" data-id="element-2418">
                                                <button type="button" onClick={handleSaveEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded transition-colors" data-id="element-2419">
                                                  Gem
                                                </button>
                                                <button type="button" onClick={handleCancelEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-stone-600 bg-stone-100 hover:bg-stone-200 rounded transition-colors" data-id="element-2420">
                                                  Annuller
                                                </button>
                                              </div>
                                            </div> : <div className="flex items-start justify-between gap-3" data-id="element-2421">
                                              <div className="flex-1" data-id="element-2422">
                                                <div className="flex items-center gap-2 mb-1" data-id="element-2423">
                                                  <CalendarIcon className="w-3.5 h-3.5 text-teal-600" data-id="element-2424" />
                                                  <span className="text-xs font-medium text-teal-700" data-id="element-2425">
                                                    {new Date(entry.date).toLocaleDateString('da-DK', {
                                        weekday: 'short',
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                                  </span>
                                                  {entry.hours && <>
                                                      <span className="text-xs text-stone-400" data-id="element-2426">
                                                        •
                                                      </span>
                                                      <ClockIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-2427" />
                                                      <span className="text-xs font-medium text-blue-700" data-id="element-2428">
                                                        {entry.hours}t
                                                      </span>
                                                    </>}
                                                </div>
                                                <p className="text-sm text-stone-900" data-id="element-2429">
                                                  {entry.description}
                                                </p>
                                              </div>
                                              {!isReadOnly && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-2430">
                                                  <button type="button" onClick={() => handleStartEditWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger log" data-id="element-2431">
                                                    <EditIcon className="w-4 h-4" data-id="element-2432" />
                                                  </button>
                                                  <button type="button" onClick={() => handleRemoveWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern log" data-id="element-2433">
                                                    <XCircle className="w-4 h-4" data-id="element-2434" />
                                                  </button>
                                                </div>}
                                            </div>}
                                        </motion.li>;
                          })}
                                </ul>
                              </motion.div>}
                          </AnimatePresence>
                        </div>}
                    </div>
                  </div>
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2435">
                  <button type="button" onClick={() => setExpandedSections({
                ...expandedSections,
                workLog: true
              })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2436">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2437" />
                    Vis Arbejdslog
                    {workLog.length > 0 && <span className="text-stone-500" data-id="element-2438">
                        ({workLog.length} entries)
                      </span>}
                  </button>
                </motion.div>}
            </AnimatePresence>

            {/* ATTACHMENTS SECTION */}
            <AnimatePresence data-id="element-2439">
              {expandedSections.attachments ? <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2440">
                  <div data-id="element-2441">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  attachments: false
                })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2442">
                      <h3 className="text-lg font-semibold text-stone-900" data-id="element-2443">
                        Bilag
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-2444">
                        {attachments.length > 0 && <span className="text-sm text-stone-500" data-id="element-2445">
                            {attachments.length} bilag
                          </span>}
                        <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2446" />
                      </div>
                    </button>

                    <div className="space-y-3" data-id="element-2447">
                      {!isReadOnly && <div className="flex justify-end mb-4" data-id="element-2448">
                          <input type="file" id="file-upload" className="hidden" onChange={handleFileUpload} disabled={isUploading} data-id="element-2449" />
                          <label htmlFor="file-upload" className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer ${isUploading ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2450">
                            {isUploading ? <>
                                <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2451" />
                                Uploader...
                              </> : <>
                                <UploadIcon className="w-4 h-4" data-id="element-2452" />
                                Tilføj bilag
                              </>}
                          </label>
                        </div>}

                      {attachments.length > 0 ? <ul className="space-y-2" data-id="element-2453">
                          {attachments.map((attachment, index) => <motion.li key={attachment.storagePath} initial={{
                      opacity: 0,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} className="flex items-center justify-between bg-white p-3 rounded-lg border border-stone-200" data-id="element-2454">
                              <div className="flex items-center gap-3 overflow-hidden" data-id="element-2455">
                                <div className="p-2 bg-stone-100 rounded-lg flex-shrink-0" data-id="element-2456">
                                  <FileIcon className="w-5 h-5 text-stone-600" data-id="element-2457" />
                                </div>
                                <div className="min-w-0" data-id="element-2458">
                                  <p className="text-sm font-medium text-stone-900 truncate" data-id="element-2459">
                                    {attachment.name}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-stone-500" data-id="element-2460">
                                    <span data-id="element-2461">
                                      {new Date(attachment.uploadedAt).toLocaleDateString('da-DK', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                                    </span>
                                    {attachment.size && <>
                                        <span data-id="element-2462">•</span>
                                        <span data-id="element-2463">
                                          {formatFileSize(attachment.size)}
                                        </span>
                                      </>}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-shrink-0 ml-4" data-id="element-2464">
                                <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="p-2 text-stone-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Download" data-id="element-2465">
                                  <DownloadIcon className="w-4 h-4" data-id="element-2466" />
                                </a>
                                {!isReadOnly && <button type="button" onClick={() => handleDeleteAttachment(attachment, index)} className="p-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Slet" data-id="element-2467">
                                    <Trash2Icon className="w-4 h-4" data-id="element-2468" />
                                  </button>}
                              </div>
                            </motion.li>)}
                        </ul> : <div className="text-center py-8 bg-stone-50 rounded-lg border border-stone-200" data-id="element-2469">
                          <PaperclipIcon className="w-8 h-8 text-stone-400 mx-auto mb-2" data-id="element-2470" />
                          <p className="text-sm text-stone-500" data-id="element-2471">
                            Ingen bilag tilføjet endnu
                          </p>
                        </div>}
                    </div>
                  </div>
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2472">
                  <button type="button" onClick={() => setExpandedSections({
                ...expandedSections,
                attachments: true
              })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2473">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2474" />
                    Vis Bilag
                    {attachments.length > 0 && <span className="text-stone-500" data-id="element-2475">
                        ({attachments.length} bilag)
                      </span>}
                  </button>
                </motion.div>}
            </AnimatePresence>

            {/* Vigtige bemærkninger */}
            <div data-id="element-2476">
              <RichTextEditor label="Vigtige bemærkninger" value={subTasks} onChange={setSubTasks} placeholder="Vigtige bemærkninger om opgaven" rows={3} disabled={isReadOnly} data-id="element-2477" />
            </div>
          </form>
        </div>
      </div>

      {/* Bottom action buttons */}
      <div className="mt-8 flex gap-3" data-id="element-2478">
        <Button type="button" variant="secondary" onClick={onBack} className="flex-1" data-id="element-2479">
          Annuller
        </Button>
        <Button type="button" variant="primary" onClick={handleSubmit as any} className="flex-1" disabled={isReadOnly} data-id="element-2480">
          Gem ændringer
        </Button>
      </div>
    </div>;
  <div data-id="element-2481">;</div>;
}